/**
 * Per-message script detection. Used to set the text direction of a single chat bubble
 * independently of the UI language (an Arabic UI can show an English bubble LTR and vice versa).
 */
export type Script = 'ar' | 'latin' | 'neutral';
export type Direction = 'rtl' | 'ltr';

// Built from code-point escapes inside strings so the source file holds no literal RTL or
// irregular-whitespace characters (U+FEFF is part of the Arabic Presentation Forms-B block).
const ARABIC = new RegExp(
  '[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]',
  'g',
);
const LATIN = new RegExp('[A-Za-z\u00C0-\u024F]', 'g');

export function detectScript(text: string): Script {
  const arabic = text.match(ARABIC)?.length ?? 0;
  const latin = text.match(LATIN)?.length ?? 0;
  if (arabic === 0 && latin === 0) return 'neutral';
  return arabic >= latin ? 'ar' : 'latin';
}

/** Direction for a bubble; `fallback` is the UI direction for neutral text (numbers, emoji). */
export function directionForText(text: string, fallback: Direction = 'ltr'): Direction {
  const script = detectScript(text);
  if (script === 'ar') return 'rtl';
  if (script === 'latin') return 'ltr';
  return fallback;
}

/** BCP-47 language tag guess for an event's language_tags. */
export function languageTagForText(text: string): 'ar' | 'en' | undefined {
  const script = detectScript(text);
  if (script === 'ar') return 'ar';
  if (script === 'latin') return 'en';
  return undefined;
}
